import React, { useEffect, useRef } from 'react';
function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
export function WhyShopsFail() {
  const headerRef = useScrollReveal(0);
  const card1Ref = useScrollReveal(100);
  const card2Ref = useScrollReveal(200);
  const card3Ref = useScrollReveal(300);
  const closingRef = useScrollReveal(400);
  return <section className="w-full py-20 sm:py-28" style={{
    backgroundColor: '#0f0f0f'
  }} aria-label="Why most barbershops keep letting you down" data-id="element-564">
      <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-565">
        <div ref={headerRef} data-id="element-566">
          <div className="flex items-center gap-3 mb-6" data-id="element-567">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-568" />
            <span className="section-label" data-id="element-569">The Real Reason</span>
          </div>
          <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
          fontWeight: 700,
          color: '#F5F5F5',
          lineHeight: 1.2,
          marginBottom: '1rem'
        }} data-id="element-570">
            Why Most Barbershops{' '}
            <span style={{
            color: '#C9A84C',
            fontStyle: 'italic'
          }} data-id="element-571">
              Keep Letting You Down
            </span>
          </h2>
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.05rem',
          color: '#888',
          lineHeight: 1.7,
          marginBottom: '3rem'
        }} data-id="element-572">
            Let's be real about why the same problem keeps repeating itself.
          </p>
        </div>

        <div className="space-y-5" data-id="element-573">
          <div ref={card1Ref} data-id="element-574">
            <FailCard number="01" title="Chain Salons" description="Hire whoever's available, rotate stylists constantly, and follow a script. They don't know the difference between a skin fade and a mid fade — and they won't ask." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-576">
                  <rect x="2" y="3" width="20" height="14" rx="2" data-id="element-577" />
                  <line x1="8" y1="21" x2="16" y2="21" data-id="element-578" />
                  <line x1="12" y1="17" x2="12" y2="21" data-id="element-579" />
                </svg>} data-id="element-575" />
          </div>
          <div ref={card2Ref} data-id="element-580">
            <FailCard number="02" title="Random Walk-In Shops" description="Inconsistent by nature. The guy who did you right last month is gone. The new guy is 'still learning.' Your head is his classroom." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-582">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-583" />
                  <line x1="12" y1="9" x2="12" y2="13" data-id="element-584" />
                  <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-585" />
                </svg>} data-id="element-581" />
          </div>
          <div ref={card3Ref} data-id="element-586">
            <FailCard number="03" title="High-End Appointment-Only Shops" description="Make you plan your haircut like a dentist visit. Life doesn't schedule itself. Sometimes you need to be fresh today — not next Thursday at 3pm." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-588">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" data-id="element-589" />
                  <line x1="16" y1="2" x2="16" y2="6" data-id="element-590" />
                  <line x1="8" y1="2" x2="8" y2="6" data-id="element-591" />
                  <line x1="3" y1="10" x2="21" y2="10" data-id="element-592" />
                  <line x1="10" y1="15" x2="14" y2="15" data-id="element-593" />
                </svg>} data-id="element-587" />
          </div>
        </div>

        <div ref={closingRef} className="mt-10 p-6 sm:p-8 rounded-sm" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-594">
          <p style={{
          fontFamily: 'var(--font-callout)',
          fontSize: 'clamp(1.1rem, 2.5vw, 1.35rem)',
          fontWeight: 600,
          color: '#F5F5F5',
          lineHeight: 1.6,
          letterSpacing: '0.02em'
        }} data-id="element-595">
            And through all of it, you keep starting over. Explaining your hair.
            Hoping this time it's different.
          </p>
          <p style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
          fontStyle: 'italic',
          color: '#C9A84C',
          marginTop: '1rem'
        }} data-id="element-596">
            It doesn't have to be this way.
          </p>
        </div>
      </div>
    </section>;
}
function FailCard({
  number,
  title,
  description,
  icon
}: {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}) {
  return <div className="flex gap-5 p-6 rounded-sm" style={{
    backgroundColor: '#111111',
    border: '1px solid rgba(139,26,26,0.2)'
  }} data-id="element-597">
      <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-598">
        {icon}
        <span style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: 'rgba(139,26,26,0.6)',
        letterSpacing: '0.1em'
      }} data-id="element-599">
          {number}
        </span>
      </div>
      <div data-id="element-600">
        <h3 style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#F5F5F5',
        letterSpacing: '0.04em',
        marginBottom: '0.5rem'
      }} data-id="element-601">
          {title}
        </h3>
        <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.95rem',
        color: '#888',
        lineHeight: 1.7
      }} data-id="element-602">
          {description}
        </p>
      </div>
    </div>;
}